import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import {
  DsmProductLibraryBulkVisibility,
  DsmProductLibraryCategory,
  DsmProductLibrarySettingsUpdate,
  DsmProductLibrarySubcategory
} from '../models/dsm-product-library.models';

@Injectable({ providedIn: 'root' })
export class DsmProductLibraryService {
  readonly #api = inject(ApiCallingV2Service);
  readonly #baseUrl = `${inject(API_CONFIG).baseUrlV2}/dsm/library/settings`;

  readCategories(
    productListId: string,
    includeHidden = true,
    includeInactive = true,
    showLoader = false
  ): Observable<DsmProductLibraryCategory[]> {
    return this.#api.getData<DsmProductLibraryCategory[]>(
      `${this.#baseUrl}/product-lists/${productListId}/categories?includeHidden=${includeHidden}&includeInactive=${includeInactive}`,
      {
        showLoader,
        showMessage: false
      }
    );
  }

  readSubcategories(
    categoryId: string,
    includeHidden = true,
    includeInactive = true,
    showLoader = false
  ): Observable<DsmProductLibrarySubcategory[]> {
    return this.#api.getData<DsmProductLibrarySubcategory[]>(
      `${this.#baseUrl}/categories/${categoryId}/subcategories?includeHidden=${includeHidden}&includeInactive=${includeInactive}`,
      {
        showLoader,
        showMessage: false
      }
    );
  }

  readSubcategoriesByCategories(
    categoryIds: string[],
    includeHidden = true,
    includeInactive = true,
    showLoader = true
  ): Observable<DsmProductLibrarySubcategory[]> {
    const categoryIdsQuery = categoryIds.map(id => `categoryIds=${encodeURIComponent(id)}`).join('&');

    return this.#api.getData<DsmProductLibrarySubcategory[]>(
      `${this.#baseUrl}/subcategories?${categoryIdsQuery}&includeHidden=${includeHidden}&includeInactive=${includeInactive}`,
      {
        showLoader,
        showMessage: false
      }
    );
  }

  /** Returns the recomputed row — assign it straight back into the table. */
  saveCategory(categoryId: string, body: DsmProductLibrarySettingsUpdate): Observable<DsmProductLibraryCategory> {
    return this.#api.putData<DsmProductLibraryCategory>(`${this.#baseUrl}/categories/${categoryId}`, body, {
      showLoader: false,
      showMessage: true,
      message: 'Saved'
    });
  }

  saveSubcategory(
    subcategoryId: string,
    body: DsmProductLibrarySettingsUpdate
  ): Observable<DsmProductLibrarySubcategory> {
    return this.#api.putData<DsmProductLibrarySubcategory>(`${this.#baseUrl}/subcategories/${subcategoryId}`, body, {
      showLoader: false,
      showMessage: true,
      message: 'Saved'
    });
  }

  /** All-or-nothing: an unknown id rejects the whole request and writes nothing. */
  setCategoriesVisibility(body: DsmProductLibraryBulkVisibility): Observable<DsmProductLibraryCategory[]> {
    return this.#api.putData<DsmProductLibraryCategory[]>(`${this.#baseUrl}/categories/visibility`, body, {
      showLoader: false,
      showMessage: true,
      message: 'Updated'
    });
  }

  setSubcategoriesVisibility(body: DsmProductLibraryBulkVisibility): Observable<DsmProductLibrarySubcategory[]> {
    return this.#api.putData<DsmProductLibrarySubcategory[]>(`${this.#baseUrl}/subcategories/visibility`, body, {
      showLoader: false,
      showMessage: true,
      message: 'Updated'
    });
  }
}
